import React from 'react'
import { array } from 'prop-types'
import intl from 'react-intl-universal'

import { branch } from 'baobab-react/higher-order'
import { sortBy } from 'lodash'
import Tour from 'utils/monkey/reactour'
import API from 'utils/API'
import csrfToken from 'utils/csrfToken'
import { detectStopScroll } from 'utils/bodyScrollLock'
import isDOMAvailable from 'utils/isDOMAvailable'

import BaseTemplate from 'components/templates/BaseTemplate'
import OnboardingTooltip from 'components/molecules/OnboardingTooltip'

import Container from 'components/atoms/Container'
import Typography from 'components/atoms/Typography'
import AuthenticatedLink from 'components/molecules/AuthenticatedLink'
import AptitudeHistoryItem from 'components/organisms/AptitudeHistoryItem'
import AptitudeEmptyPlaceholder from 'components/organisms/AptitudeEmptyPlaceholder'

import Select from 'react-select'

class AptitudeHistory extends React.Component {
  state = {
    sortParam: null,
    isTourOpen: false,
    mobileView: null,
  }

  componentDidMount() {
    const type = 'aptitude-history'
    if (this.checkTour(type)) {
      const options = {
        headers: { 'X-CSRF-Token': csrfToken() },
      }
      API.post(API.endPoints({ type }).auth.onboarding_tooltip, {}, options).then(() => {
        this.setState({ isTourOpen: true })
        this.isMobileView()
      })
      detectStopScroll(this.disableBody)
    }
  }

  closeTour = () => {
    this.setState({ isTourOpen: false })
    document.body.style.overflow = ''
  }

  disableBody = () => {
    if (this.state.isTourOpen) {
      document.body.style.overflow = 'hidden'
    }
  }

  checkTour = (type) => {
    const { history, onboardingTooltip } = this.props
    return !!(onboardingTooltip && !onboardingTooltip.includes(type) && history.length > 0)
  }

  setMobileView = (value) => {
    this.setState({ mobileView: value })
  }

  sortOptions = () => {
    const characterInitials = 'RIASEC'
    return characterInitials.split('').map(character => ({
      value: character,
      label: `Urutan berdasar ${character}`,
    }))
  }

  setSortParam = (value) => {
    this.setState({ sortParam: value })
  }

  handleChange = (character) => {
    character ? this.setSortParam(character.value) : this.setSortParam(character)
  }

  sortedHistory = () => {
    const { sortParam } = this.state
    const { history } = this.props
    if (sortParam) {
      return sortBy(history, `ratio.${sortParam}`).reverse()
    }
    return history.map((h, idx) => ({ ...h, latest: idx === 0 }))
  }

  isMobileView() {
    (isDOMAvailable && window.outerWidth < 768) ? this.setMobileView(true) : this.setMobileView(false)
  }

  render() {
    const { history } = this.props
    const { mobileView, sortParam } = this.state
    const detailTestSelector = mobileView ? '.aptitude-history-item__link-detail' : '.aptitude-history-item__link:first-child'
    const newTestSelector = mobileView ? '.aptitude-history__new-test' : '.aptitude-history__new-test-btn'
    const titleLeft = mobileView ? '0' : '10%'
    const marginTopPC = mobileView ? '10px' : '20px'
    const leftPositionPC = mobileView ? '0' : '-5%'
    const tourProps = {
      isOpen: this.state.isTourOpen,
      onRequestClose: this.closeTour,
      showNavigation: false,
      showNumber: false,
      showButtons: false,
      showCloseButton: false,
      showNavigationNumber: false,
      disableInteraction: true,
      disableKeyboardNavigation: true,
      rounded: 4,
      closeWithMask: false,
      maskSpace: 8,
      steps: [
        {
          selector: '.aptitude-history__title-sort',
          content: ({ goTo }) => (
            <OnboardingTooltip
              text="Sekarang kamu bisa mengurutkan hasil berdasarkan nilai RIASEC kamu"
              arrow="top"
              closeFunc={this.closeTour}
              nextFunc={() => goTo(1)}
            />
          ),
          position: 'bottom',
          style: {
            padding: '16px',
            marginTop: marginTopPC,
            boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.1)',
            left: titleLeft,
          },
        },
        {
          selector: detailTestSelector,
          content: ({ goTo }) => (
            <OnboardingTooltip
              text="Tekan tombol selengkapnya untuk melihat hasil lengkap Tes Minat Bakat kamu"
              arrow="bottom"
              backFunc={() => goTo(0)}
              nextFunc={() => goTo(2)}
            />
          ),
          position: 'top',
          style: {
            padding: '16px',
            boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.1)',
            left: leftPositionPC,
            top: '-2%',
          },
        },
        {
          selector: newTestSelector,
          content: ({ goTo }) => (
            <OnboardingTooltip
              text="Tekan tombol ini untuk memulai Tes Minat Bakat baru"
              arrow="top"
              completeFunc={this.closeTour}
              backFunc={() => goTo(1)}
            />
          ),
          position: 'bottom',
          style: {
            padding: '16px',
            boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.1)',
            marginTop: marginTopPC,
            left: leftPositionPC,
          },
        },
      ],
    }
    return (
      <BaseTemplate useNewHeader>
        <Tour {...tourProps} />
        <div className="aptitude-history">
          <div className="aptitude-history__page-heading">
            <Container>
              <div className="aptitude-history__heading-content">
                <div className="aptitude-history__title-sort">
                  <Typography component="h1" variant="headline">
                    Riwayat Tes Minat Bakat
                  </Typography>
                  <Select
                    className="aptitude-history__sort-opts"
                    placeholder="Urutkan"
                    options={this.sortOptions()}
                    onChange={this.handleChange}
                    isClearable
                  />
                </div>
                <div className="aptitude-history__heading-new-test">
                  <AuthenticatedLink href="/aptitude_test/instruction" className="button button-default aptitude-history__new-test-btn">
                    {intl.get('aptitude.result.history.take_new_test')}
                  </AuthenticatedLink>
                </div>
              </div>
            </Container>
          </div>

          {
            (history.length === 0) &&
            <AptitudeEmptyPlaceholder />
          }

          {
            history.length > 0 &&
            <Container className="aptitude-history__list">
              <br/>
              {
                history.length > 0 && this.sortedHistory().map((obj, idx) => (
                  <AptitudeHistoryItem
                    id={obj.id}
                    date={obj.created_at}
                    ratio={obj.ratio}
                    key={`apt-history-${obj.id}`}
                    latest={obj.latest}
                    dataTestId={`detail-test-history-${idx + 1}`}
                  />
                ))
              }
              <div className="aptitude-history__new-test">
                <AuthenticatedLink href="/aptitude_test/instruction" className="aptitude-history__new-test-btn">
                  {intl.get('aptitude.result.history.take_new_test')}
                </AuthenticatedLink>
              </div>
            </Container>
          }
        </div>

        <style jsx>
          {`
            .aptitude-history {
              padding-bottom: 64px;
              &__page-heading {
                background-color: #fff;
                padding: 24px 0;
              }
              &__heading-content {
                display: flex;
                justify-content: space-between;
                align-items: center;
              }
              &__heading-new-test {
                display: none;
                @media screen and (min-width: 768px) {
                  display: unset;
                }
              }
              &__title-sort {
                display: flex;
                align-items: center;
                :global(h1) {
                  color: #1e3f6b;
                  font-size: 18px !important;
                  @media screen and (max-width: 767px) {
                    font-size: 16px !important;
                  }
                }
              }
              :global(&__list) {
                font-weight: bold;
              }
              &__new-test {
                text-align: center;
                margin-top: 32px;
              }
              :global(&__new-test-btn) {
                font-weight: bold;
                padding: 12px 24px;
              }
              :global(&__sort-opts) {
                flex-grow: 1;
                width: 128px;
                margin-left: 16px;
                @media screen and (min-width: 768px) {
                  width: 232px;
                }
              }
              :global(&__sort-opts>div) {
                padding: 2px 0;
              }
              :global(.aptitude-history-item:first-of-type) {
                box-shadow: ${!sortParam ? '0 0 8px 0 rgba(0, 190, 255, 0.55)' : 'inherit'};
                background-color: ${!sortParam ? '#f0fbff' : '#fff'};
              }
            }
          `}
        </style>
      </BaseTemplate>
    )
  }
}

AptitudeHistory.propTypes = {
  history: array,
  onboardingTooltip: array,
}

AptitudeHistory.defaultProps = {
  history: [],
}

export { AptitudeHistory }
export default branch({
  history: ['serverProps', 'history'],
  onboardingTooltip: ['serverProps', 'currentUser', 'tooltip'],
}, AptitudeHistory)
